import { Router } from '@angular/router';
import { EReducers, IReducers } from './../reducers/index';
import { Store } from '@ngrx/store';
import { IUserSessionData, UserState, UserType } from 'app/store/models/user.model';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { getUserSession } from '../actions/user.action';
import { distinctUntilChanged, takeUntil, debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  ready: BehaviorSubject<IUserSessionData>;
  changes: BehaviorSubject<IUserSessionData> = new BehaviorSubject<IUserSessionData>(undefined);

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private inboxStatusRefresh$ = new Subject<void>();

  constructor(
    private store: Store<IReducers>,
    private router: Router,
  ) {
    this.inboxStatusRefresh$
      .pipe(debounceTime(400))
      .subscribe(() => {
        const session = this.changes.getValue();
        if (session?.user_type === UserType.SINGLE_USER) {
          this.store.dispatch(getUserSession());
        }
      });
  }

  isImpersonating(): boolean {
    let oldTokensData = localStorage.getItem('oldTokens');

    try {
      oldTokensData = JSON.parse(oldTokensData);
    } catch (e) {
      console.error(e);
    }

    if (Array.isArray(oldTokensData) && oldTokensData.length === 0) {
      return false;
    }

    return !!oldTokensData;
  }

  onReady(): Observable<IUserSessionData> {
    if (!this.ready) {
      // @ts-ignore
      return of({});
    }
    return this.ready.asObservable();
  }

  onChanges(): Observable<IUserSessionData> {
    return this.changes.asObservable();
  }

  /** Reload session so the inbox indicator picks up group and campaign changes. */
  refreshInboxStatus(): void {
    if (this.changes.getValue()?.user_type === UserType.SINGLE_USER) {
      this.inboxStatusRefresh$.next();
    }
  }

  loadUserSession() {
    this.ready = new BehaviorSubject<IUserSessionData>(undefined);
    this.store
      .select(EReducers.USER)
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((x, y) => {
          if (x.sessionData === undefined || y.sessionData === undefined) {
            return false;
          }

          return (
            JSON.stringify(x.sessionData) === JSON.stringify(y.sessionData)
          );
        }),
      )
      .subscribe((state: UserState) => {
        if (!state.loggedIn && window.location.pathname !== '/login') {
          if (environment.production) {
            window.location.href = 'https://retreva.com';
            return;
          }
          this.router.navigate(['/login']);
          return;
        }

        if (state.sessionData === undefined) {
          this.store.dispatch(getUserSession());
          return;
        } else {
          this.ready.next(state.sessionData);
          this.changes.next(state.sessionData);
        }
      });
  }

  destroySession() {
    this.destroy$.next(false);
  }
}
